import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { FormattedMessage, useIntl } from "react-intl";
import { MultiColumnList, Button, RadioButton, MessageBanner, Loading, } from "@folio/stripes/components";
import { useOkapiKy } from "@folio/stripes/core";
import { fetchOwnCloudTitleFiles, importTitlesFromOwnCloud, } from "../../api/titleImportService";
import { classifyOwnCloudImportError, extractBackendErrorMessages, getHttpStatus, } from "../../utils/importErrors";
export var OwnCloudTitleImportSection = function (_a) {
    var onImportStarted = _a.onImportStarted;
    var formatMessage = useIntl().formatMessage;
    var queryClient = useQueryClient();
    var kyInstance = useOkapiKy();
    var _b = useState(null), selectedFile = _b[0], setSelectedFile = _b[1];
    var _c = useState(null), successMessage = _c[0], setSuccessMessage = _c[1];
    var _d = useState(null), noticeMessage = _d[0], setNoticeMessage = _d[1];
    var _e = useState(null), errorMessage = _e[0], setErrorMessage = _e[1];
    var _f = useQuery(["ownCloudTitleFiles"], function () { return fetchOwnCloudTitleFiles(kyInstance); }, { retry: false }), filesData = _f.data, isLoading = _f.isLoading, isError = _f.isError, error = _f.error;
    var is503Error = isError && getHttpStatus(error) === 503;
    var _g = useMutation(function (fileName) { return importTitlesFromOwnCloud(kyInstance, fileName); }, {
        onSuccess: function () {
            setSuccessMessage(formatMessage({
                id: "ui-br-agency.import.titleImport.owncloud.success",
            }));
            setNoticeMessage(null);
            setErrorMessage(null);
            setSelectedFile(null);
            queryClient.invalidateQueries(["ownCloudTitleFiles"]);
            onImportStarted();
        },
        onError: function (err) {
            setSuccessMessage(null);
            var kind = classifyOwnCloudImportError(err);
            if (kind === "notFound") {
                setNoticeMessage(null);
                setErrorMessage(formatMessage({
                    id: "ui-br-agency.import.titleImport.owncloud.error.notFound",
                }));
            }
            else if (kind === "notConfigured") {
                setNoticeMessage(null);
                setErrorMessage(formatMessage({
                    id: "ui-br-agency.import.titleImport.owncloud.notConfigured",
                }));
            }
            else if (kind === "rejected" || kind === "alreadyRunning") {
                // The backend refused to start the import (422: a precondition failed,
                // 409: another import is running). Nothing is running for this request,
                // so show the backend's reason and keep the file selected for a retry.
                setNoticeMessage(null);
                var fallbackId_1 = kind === "rejected"
                    ? "ui-br-agency.import.titleImport.owncloud.error.rejected"
                    : "ui-br-agency.import.titleImport.owncloud.error.alreadyRunning";
                extractBackendErrorMessages(err).then(function (messages) {
                    setErrorMessage(messages.length > 0
                        ? messages.join(" ")
                        : formatMessage({ id: fallbackId_1 }));
                });
            }
            else {
                // Timeout / network / gateway error: the backend returns 202 immediately
                // and runs the download + import on a background thread, so a failed POST
                // does NOT mean the import failed. Switch to the progress view and let
                // status polling report the real outcome (a genuine break shows up there
                // as status = FAILED). Never show the hard error banner here.
                setErrorMessage(null);
                setNoticeMessage(formatMessage({
                    id: "ui-br-agency.import.titleImport.owncloud.startedUnconfirmed",
                }));
                setSelectedFile(null);
                queryClient.invalidateQueries(["ownCloudTitleFiles"]);
                onImportStarted();
            }
        },
    }), triggerImport = _g.mutate, isImporting = _g.isLoading;
    var handleImport = function () {
        if (selectedFile) {
            triggerImport(selectedFile);
        }
    };
    var handleRefresh = function () {
        queryClient.invalidateQueries(["ownCloudTitleFiles"]);
    };
    var formatFileSize = function (bytes) {
        if (bytes === undefined || bytes === null)
            return "-";
        if (bytes < 1024)
            return "".concat(bytes, " B");
        if (bytes < 1024 * 1024)
            return "".concat((bytes / 1024).toFixed(1), " KB");
        return "".concat((bytes / (1024 * 1024)).toFixed(1), " MB");
    };
    var formatDate = function (dateString) {
        if (!dateString)
            return "-";
        try {
            var date = new Date(dateString);
            return date.toLocaleDateString("cs-CZ", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        }
        catch (_a) {
            return dateString;
        }
    };
    if (isLoading) {
        return React.createElement(Loading, null);
    }
    if (is503Error) {
        return (React.createElement(MessageBanner, { type: "warning" },
            React.createElement(FormattedMessage, { id: "ui-br-agency.import.titleImport.owncloud.notConfigured" })));
    }
    if (isError) {
        return (React.createElement(MessageBanner, { type: "error" },
            React.createElement(FormattedMessage, { id: "ui-br-agency.import.titleImport.owncloud.error" })));
    }
    var files = (filesData === null || filesData === void 0 ? void 0 : filesData.files) || [];
    if (files.length === 0) {
        return (React.createElement(React.Fragment, null,
            React.createElement(MessageBanner, { type: "default" },
                React.createElement(FormattedMessage, { id: "ui-br-agency.import.titleImport.owncloud.noFiles" })),
            React.createElement("div", { style: { marginTop: "1rem" } },
                React.createElement(Button, { onClick: handleRefresh },
                    React.createElement(FormattedMessage, { id: "ui-br-agency.button.refresh" })))));
    }
    var columnMapping = {
        selected: "",
        name: formatMessage({
            id: "ui-br-agency.import.titleImport.owncloud.column.name",
        }),
        size: formatMessage({
            id: "ui-br-agency.import.titleImport.owncloud.column.size",
        }),
        lastModified: formatMessage({
            id: "ui-br-agency.import.titleImport.owncloud.column.lastModified",
        }),
    };
    var formatter = {
        selected: function (item) { return (React.createElement(RadioButton, { checked: selectedFile === item.name, onChange: function () { return setSelectedFile(item.name); }, "aria-label": formatMessage({ id: "ui-br-agency.import.titleImport.owncloud.select.aria" }, { fileName: item.name }) })); },
        name: function (item) { return item.name; },
        size: function (item) { return formatFileSize(item.size); },
        lastModified: function (item) { return formatDate(item.lastModified); },
    };
    return (React.createElement(React.Fragment, null,
        successMessage && (React.createElement(MessageBanner, { type: "success", dismissible: true, onExited: function () { return setSuccessMessage(null); } }, successMessage)),
        noticeMessage && (React.createElement(MessageBanner, { type: "warning", dismissible: true, onExited: function () { return setNoticeMessage(null); } }, noticeMessage)),
        errorMessage && (React.createElement(MessageBanner, { type: "error", dismissible: true, onExited: function () { return setErrorMessage(null); } }, errorMessage)),
        React.createElement(MultiColumnList, { contentData: files, columnMapping: columnMapping, formatter: formatter, visibleColumns: ["selected", "name", "size", "lastModified"], columnWidths: {
                selected: "50px",
                name: "40%",
                size: "15%",
                lastModified: "25%",
            }, interactive: false }),
        React.createElement("div", { style: { marginTop: "1rem", display: "flex", gap: "1rem" } },
            React.createElement(Button, { buttonStyle: "primary", onClick: handleImport, disabled: !selectedFile || isImporting }, isImporting ? (React.createElement(FormattedMessage, { id: "ui-br-agency.import.titleImport.owncloud.button.importing" })) : (React.createElement(FormattedMessage, { id: "ui-br-agency.import.titleImport.owncloud.button.import" }))),
            React.createElement(Button, { onClick: handleRefresh, disabled: isImporting },
                React.createElement(FormattedMessage, { id: "ui-br-agency.button.refresh" })))));
};
